import { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { cn } from "@shared/utils/cn";

// Fix para los iconos de marcadores en Leaflet
// Los iconos por defecto no funcionan con Vite/webpack sin esta configuración
import icon from "leaflet/dist/images/marker-icon.png";
import iconShadow from "leaflet/dist/images/marker-shadow.png";

const DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  tooltipAnchor: [16, -28],
  shadowSize: [41, 41],
});

L.Marker.prototype.options.icon = DefaultIcon;

export interface InteractiveMapProps {
  /**
   * Coordenadas del punto a marcar [latitud, longitud]
   * Ejemplo: [-34.6037, -58.3816] para Buenos Aires
   */
  position: [number, number];
  /**
   * Nombre del lugar (se mostrará en el popup del marcador)
   */
  placeName?: string;
  /**
   * Dirección o información adicional (se mostrará en el popup)
   */
  address?: string;
  /**
   * Nivel de zoom inicial (1-18, por defecto 13)
   */
  zoom?: number;
  /**
   * Altura del mapa en píxeles (por defecto 400px)
   */
  height?: string;
  /**
   * Clases CSS adicionales
   */
  className?: string;
  /**
   * Si es true, el usuario no puede hacer zoom ni arrastrar el mapa
   */
  interactive?: boolean;
}

/**
 * Componente de mapa interactivo usando Leaflet y OpenStreetMap
 * No requiere API key ni integración con Google Maps
 * 
 * @example
 * ```tsx
 * <InteractiveMap
 *   position={[-34.6037, -58.3816]}
 *   placeName="Cerámicas Gassull"
 *   address="Av. Corrientes 1234, Buenos Aires, Argentina"
 *   zoom={15}
 * />
 * ```
 */
export function InteractiveMap({
  position,
  placeName = "Ubicación",
  address,
  zoom = 13,
  height = "400px",
  className,
  interactive = true,
}: InteractiveMapProps) {
  const mapRef = useRef<L.Map | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    // Crear el mapa
    const map = L.map(containerRef.current, {
      center: position,
      zoom,
      zoomControl: interactive,
      dragging: interactive,
      touchZoom: interactive,
      doubleClickZoom: interactive,
      scrollWheelZoom: interactive,
      boxZoom: interactive,
      keyboard: interactive,
    });

    // Agregar capa de tiles de OpenStreetMap
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 19,
    }).addTo(map);

    // Crear el marcador
    const marker = L.marker(position).addTo(map);

    // Agregar popup con información
    const popupContent = `
      <div style="padding: 8px;">
        <strong style="font-size: 14px; font-weight: 600; color: #1f2937; display: block; margin-bottom: 4px;">
          ${placeName}
        </strong>
        ${address ? `<p style="font-size: 12px; color: #6b7280; margin: 0;">${address}</p>` : ""}
      </div>
    `;
    marker.bindPopup(popupContent).openPopup();

    mapRef.current = map;

    // Cleanup al desmontar
    return () => {
      map.remove();
    };
  }, [position, placeName, address, zoom, interactive]);

  return (
    <div
      ref={containerRef}
      className={cn("w-full rounded-lg overflow-hidden border border-gray-300", className)}
      style={{ height }}
    />
  );
}

